import type { Passage } from "./types";

export const passage: Passage = {
  slug: "matthew-6-33",
  reference: "Matthew 6:33",
  title: "Seek first the kingdom: what Matthew 6:33 promises",
  h1: 'Matthew 6:33, and what "all these things" refers to',
  description:
    "Seek first the kingdom and all these things will be added is read as a formula for provision. The things are named three verses earlier, and the list is short.",
  targets: [
    "matthew 6 33 meaning",
    "seek first the kingdom of god explained",
    "all these things will be added unto you meaning",
  ],
  published: "2026-09-03",
  standfirst:
    "The promise is real and it is smaller than it is usually made to be. Everything hangs on what all these things means, and the passage says.",
  verse: {
    text: "But seek first God's Kingdom and his righteousness; and all these things will be given to you as well.",
    ref: "Matthew 6:33",
  },
  context: [
    {
      text: "Therefore don't be anxious, saying, 'What will we eat?', 'What will we drink?' or, 'With what will we be clothed?' For the Gentiles seek after all these things; for your heavenly Father knows that you need all these things.",
      ref: "Matthew 6:31–32",
    },
    {
      text: "Therefore don't be anxious for tomorrow, for tomorrow will be anxious for itself. Each day's own evil is sufficient.",
      ref: "Matthew 6:34",
    },
  ],
  takeaways: [
    "All these things is defined in verse 31: food, drink and clothing. Not career, house, spouse or outcome — the three items the passage has been listing.",
    "The audience is people with a genuine question about whether they will eat, which is why the examples are birds and grass rather than investments.",
    "Seek first is about order rather than exclusivity. The passage assumes you are also working; it addresses what your anxiety is organised around.",
    "Verse 34 caps it at one day. The unit of provision being promised is today's, which is the same unit the Lord's Prayer asks for bread in.",
  ],
  body: [
    {
      type: "h2",
      id: "what-all-these-things-means",
      text: 'What "all these things" means',
    },
    {
      type: "p",
      text: "The phrase appears three times in four verses, and the first occurrence defines it: what will we eat, what will we drink, what will we wear. Verse 32 repeats it — your Father knows you need all these things — and verse 33 uses it a third time as shorthand for the same list.",
    },
    {
      type: "p",
      text: "So the promise is about subsistence. That is a serious promise to make to people who were genuinely uncertain about it, and it is not a promise about a salary, a house or a plan working out.",
    },
    {
      type: "h2",
      id: "seek-first-is-about-order",
      text: "Seek first is about order",
    },
    {
      type: "p",
      text: "First here is a matter of sequence and priority rather than exclusivity. The same sermon assumes people are working, paying taxes and running households; the birds and lilies are an argument about anxiety, not an argument against agriculture.",
    },
    {
      type: "p",
      text: "What is being reordered is what your worrying is organised around. The Gentiles seek after these things — meaning, they make provision the organising centre — and the alternative offered is not indifference but a different first item.",
    },
    {
      type: "p",
      text: "There is a second reason the passage keeps its promise small, and it is about who was listening. The Sermon on the Mount is preached to a crowd in a Roman province where subsistence was genuinely precarious and a bad harvest was a real possibility rather than a metaphor. A promise about career advancement would have meant nothing to most of them. A promise that they would eat meant a great deal.",
    },
    {
      type: "h2",
      id: "the-day-sized-unit",
      text: "The day-sized unit",
    },
    {
      type: "p",
      text: "Verse 34 immediately caps the whole passage at one day, and this is the part that makes it usable. Do not be anxious for tomorrow; each day has enough of its own trouble. The provision being described arrives daily, like the bread in the prayer two chapters earlier and like the manna in Exodus.",
    },
    {
      type: "p",
      text: "That is why the passage does not answer the question people usually bring to it. It is not silent about next year out of vagueness; it has deliberately declined to talk about next year.",
    },
  ],
  misreading: {
    claim:
      "That it is a formula: put God first and the things you want will follow — the job, the house, the relationship, the outcome you have been praying about.",
    correction: [
      "The reading is easy to arrive at, because all these things is left undefined if you start at verse 33. Read that way it becomes an investment: prioritise correctly and the returns arrive, which makes the seeking instrumental and makes any shortfall a diagnosis of your seeking.",
      "The passage supplies the referent three verses earlier, and it is food, drink and clothing. Everything the promise covers is on that list, and it is a list about staying alive rather than about getting on. Read honestly it is more radical rather than less: the guarantee extends to subsistence and stops there, deliberately.",
      "And the cap in verse 34 finishes the job. Tomorrow is explicitly excluded from the scope. What is promised is that today's needs are known and attended to, which is exactly the size of promise a person can test.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read 25 to 34 rather than 33. The whole passage is nine verses, takes a minute, and the definition of the promise is inside it. The verse before the section begins is also worth having: you cannot serve both God and money, which is the claim the whole argument about anxiety is attached to.",
    },
    {
      type: "p",
      text: "Then use it in the day-sized way it was written. Asking for what today needs, and coming back tomorrow, is the shape the passage recommends and the shape {a prayer for strength} follows for the same reason.",
      links: { "a prayer for strength": "/prayers/prayer-for-strength" },
    },
    {
      type: "note",
      text: "The examples the passage chooses are worth dwelling on for a moment, because they are deliberately unimpressive. Birds, which do not store anything, and wild flowers, which last a day and are then used as fuel. Neither is offered as a model of provision to imitate; both are offered as evidence that the arrangement extends to things of very little economic consequence, and the argument moves from there to you.",
    },
    {
      type: "note",
      text: "If the verse has been used on you to explain why something did not arrive, notice that the passage never promised the thing in question. That is not a technicality.",
    },
  ],
  faq: [
    {
      q: "What does all these things will be added mean?",
      a: "The things are named in verse 31: food, drink and clothing. The promise covers subsistence, and verse 34 caps it at a single day at a time.",
    },
    {
      q: "Does Matthew 6:33 promise wealth or success?",
      a: "No. The list it refers back to is food, drink and clothing, and the surrounding passage uses birds and wild flowers as its examples. Nothing in it addresses career or outcome.",
    },
    {
      q: "What does seek first mean?",
      a: "Order rather than exclusivity. The same sermon assumes people are working and running households; what is being reordered is what a person's anxiety is organised around.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the verse is printed whole rather than referred to. It reads 'God's Kingdom' where the familiar renderings have 'the kingdom of God'; the order the sentence asks for is the same in all of them.",
    },
  ],
  related: ["screen-time-limits-dont-work", "digital-sabbath"],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-peace"],
  relatedPassages: ["philippians-4-6-7", "proverbs-3-5-6"],
};
